import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import debounce from 'debounce-promise';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faPlus,
  faTimes,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';

import {
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  COMMAND,
  GAUGE,
  IMAGE,
  LOCATION,
  MAINTENANCE,
  MAP,
  MIN_MAX_TYPE_SET,
  TEXT,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
} from '../../Constants/WidgetTypes';
import TemplateService from '../../Services/TemplateService';

export const CommandsCard = memo(function CommandsCard({
  asset,
  command,
  handleAddCommand,
  handleRemoveCommand,
  index,
  selectedCommands,
}) {
  const commandOptions = () => {
    if (!asset?.gtemplate?.commands?.length) return [];
    return asset.gtemplate.commands
      .filter((c) => !selectedCommands.some((x) => x?.command?.value === c._id))
      .map((c) => ({ label: c.name, value: c._id }));
  };

  const handleColorSelect = (color) => {
    const update = { ...command, color };
    handleAddCommand(update, index);
  };

  const handleCommandSelect = (selected) => {
    const update = { ...command, command: selected };
    handleAddCommand(update, index);
  };

  const handleFriendlyNameUpdate = ({ target: { name, value } }) => {
    const update = { ...command, [name]: value };
    handleAddCommand(update, index);
  };

  return (
    <div
      className="row no-gutters align-items-center bg-background-gray rounded my-3 py-2"
      style={{ minHeight: '5rem' }}
    >
      <div className="col-1 pl-1 px-md-3">
        <span className="fa-layers fa-fw">
          <FontAwesomeIcon icon={faCircle} color="white" size="lg" />
          <span className="fa-layers-text signal-number-text font-weight-bold text-muted">
            {index + 1}
          </span>
        </span>
      </div>
      <div className="px-md-0 col-10 d-flex flex-column">
        <div className="form-group row">
          <label className="col-sm-3 col-form-label" htmlFor="command">
            Command Name
          </label>
          <div className="col-sm-8">
            <Select
              onChange={handleCommandSelect}
              options={commandOptions()}
              value={command?.command}
            />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-3 col-form-label" htmlFor="friendlyName">
            Friendly Name
          </label>
          <div className="col-sm-8">
            <input
              className="form-control"
              name="friendlyName"
              onChange={handleFriendlyNameUpdate}
              type="text"
              value={command?.friendlyName}
            />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-3 col-form-label" htmlFor="color">
            Color
          </label>
          <div className="col-sm-8 container-fluid mx-0">
            <div className="row no-gutters align-items-stretch h-100">
              {[
                'cyan-blue',
                'danger',
                'warning-yellow',
                'alert-confirmation',
              ].map((color) => (
                <div
                  className={`col-1 mr-2 d-flex align-items-center justify-content-center ${
                    command?.color === color ? 'border border-dark' : ''
                  }`}
                  key={color}
                >
                  <button
                    className={`btn h-75 w-75 btn-${color} shadow-none rounded-0`}
                    onClick={() => handleColorSelect(color)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="col-1 pl-1 pl-md-4">
        <button
          className="btn btn-link p-0"
          onClick={() => handleRemoveCommand(index)}
        >
          <FontAwesomeIcon icon={faTrashAlt} className="text-text-dark" />
        </button>
      </div>
      <style jsx>{`
        .btn:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
});

CommandsCard.propTypes = {
  asset: PropTypes.shape({
    gtemplate: PropTypes.shape({
      commands: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  command: PropTypes.shape({
    color: PropTypes.string,
    command: PropTypes.object,
    friendlyName: PropTypes.string,
  }),
  index: PropTypes.number,
  handleAddCommand: PropTypes.func,
  handleRemoveCommand: PropTypes.func,
  selectedCommands: PropTypes.arrayOf(PropTypes.object),
};

export const ConfigureAxisMaxMin = memo(function ConfigureAxisMaxMin({
  handleNameAndYScaleUpdate,
  widgetType,
  yMax,
  yMin,
}) {
  return MIN_MAX_TYPE_SET.has(widgetType) ? (
    <div className="row justify-content-between">
      <div className="form-group col-5">
        <label htmlFor="yMin">Y Axis Minimum</label>
        <input
          name="yMin"
          type="text"
          className="form-control"
          value={yMin}
          onChange={handleNameAndYScaleUpdate}
        />
      </div>
      <div className="form-group col-5">
        <label htmlFor="yMax">Y Axis Maximum</label>
        <input
          name="yMax"
          type="text"
          className="form-control"
          value={yMax}
          onChange={handleNameAndYScaleUpdate}
        />
      </div>
    </div>
  ) : null;
});

ConfigureAxisMaxMin.propTypes = {
  handleNameAndYScaleUpdate: PropTypes.func,
  widgetType: PropTypes.string,
  yMax: PropTypes.string,
  yMin: PropTypes.string,
};

const noAddButtonTypeSet = new Set([
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  IMAGE,
  LOCATION,
  MAINTENANCE,
  MAP,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
]);

export const AddDataRow = memo(function AddDataRow({
  handleAddWidgetSignal,
  selectedType,
  widgetSignals,
  widgetType,
}) {
  const addButtonDisplayText = useMemo(() => {
      switch (widgetType) {
        case TEXT_TAGS:
          return 'Tag';
        case COMMAND:
          return 'Command';
        case GAUGE:
          return 'Gauge';
        default:
          return 'Signal';
      }
    }, [widgetType]),
    containerClassName = useMemo(() => {
      if (!widgetType) return 'd-none';
      return noAddButtonTypeSet.has(widgetType) ? 'd-none' : 'row mb-2';
    }, [widgetType]),
    dataTypeDisplayText = useMemo(() => {
      switch (widgetType) {
        case GAUGE:
          return 'Gauges';
        case TEXT_TAGS:
          return 'Tags';
        case COMMAND:
          return 'Commands';
        default:
          return 'Signals';
      }
    }, [widgetType]),
    displayAddButton = useMemo(() => {
      if (!selectedType?.maxSignals) return true;
      return widgetSignals?.length < selectedType?.maxSignals;
    }, [selectedType, widgetSignals?.length]);

  return (
    <div className={containerClassName}>
      <div className="col-12">
        <span className="font-weight-bold">{dataTypeDisplayText}</span>
        {displayAddButton ? (
          <button
            className="btn modal-button-secondary py-0 ml-3 three-quarter-text"
            onClick={handleAddWidgetSignal}
          >
            Add
            <span className="text-reset ml-1">{addButtonDisplayText}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
});

AddDataRow.propTypes = {
  handleAddWidgetSignal: PropTypes.func,
  selectedType: PropTypes.shape({ maxSignals: PropTypes.number }),
  widgetSignals: PropTypes.arrayOf(PropTypes.object),
  widgetType: PropTypes.string,
};

AddDataRow.defaultProps = { selectedType: {}, widgetSignals: [] };

const barUtilizationTypeSet = new Set([
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
]);

export const BarUtilizationTimeAggregation = memo(
  function BarUtilizationTimeAggregation(props) {
    const wrapperClassName = useMemo(
      () => (barUtilizationTypeSet.has(props.widgetType) ? 'row' : 'd-none'),
      [props.widgetType]
    );

    return (
      <div className={wrapperClassName}>
        <div className="form-group col-12 col-md-5">
          <label htmlFor="selectedAggregation">Time Aggregation</label>
          <Select
            classNamePrefix="select"
            isClearable={false}
            isSearchable={false}
            name="selectedAggregation"
            options={props.timeAggregationOptions}
            onChange={props.handleAggregationSelect}
            value={props.timeAggregationOptions.find(
              ({ value }) => value === props.selectedAggregation
            )}
          />
        </div>
      </div>
    );
  }
);
BarUtilizationTimeAggregation.propTypes = {
  handleAggregationSelect: PropTypes.func,
  selectedAggregation: PropTypes.string,
  timeAggregationOptions: PropTypes.arrayOf(PropTypes.object),
  widgetType: PropTypes.string,
};

export const SignalsCard = memo(function SignalsCard({
  datapointsMap,
  gtemplateID,
  handleDatapointSelect,
  handleOptionDisabled,
  handleRemoveWidgetSignal,
  handleSignalSelect,
  index,
  orgID,
  row,
  updateState,
}) {
  const [requestPending, setRequestPending] = useState(false);
  const service = useMemo(() => new TemplateService(), []);
  let source = CancelToken.source();

  const fetchOutputSignals = async (searchTerm) => {
    try {
      setRequestPending(true);
      const params = {
        limit: 100,
        orgID,
        search: 'name',
        searchTerm,
      };

      source.cancel();
      source = CancelToken.source();
      const { data } = await service.searchOutputSignals(
        gtemplateID,
        params,
        source.token
      );
      setRequestPending(false);
      return data.map((os) => ({ label: os.name, value: os }));
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({ ...prevState, error }));
      setRequestPending(false);
      return [];
    }
  };
  useEffect(() => source?.cancel, []);

  return (
    <div
      className="row no-gutters align-items-center bg-background-gray rounded my-3 py-2 py-md-0"
      style={{ minHeight: '5rem' }}
    >
      <div className="col-1 pl-1 px-md-3">
        <span className="fa-layers fa-fw">
          <FontAwesomeIcon icon={faCircle} color="white" size="lg" />
          <span className="fa-layers-text signal-number-text font-weight-bold text-muted">
            {index + 1}
          </span>
        </span>
      </div>
      <div className="px-md-0 col-10 d-flex flex-column flex-md-row">
        <div className="flex-fill pr-md-2">
          <label htmlFor={`signalCollection-${index}`}>Signal Collection</label>
          <Select
            captureMenuScroll={false}
            isSearchable
            maxMenuHeight={200}
            menuPortalTarget={document.body}
            name={`signalCollection-${index}`}
            options={datapointsMap}
            onChange={(e) => handleDatapointSelect(e, index)}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            value={row?.datapoint}
          />
        </div>
        <div className="flex-fill pl-md-2">
          <label htmlFor={`signal-${index}`}>Signal to track</label>
          {row?.datapoint?.value === 'outputSignal' ? (
            <AsyncSelect
              cacheOptions
              captureMenuScroll={false}
              defaultOptions
              isLoading={requestPending}
              loadOptions={debounce(fetchOutputSignals, 1000)}
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              name={`signal-${index}`}
              onChange={(e) => handleSignalSelect(e, index)}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              value={row?.signal}
            />
          ) : (
            <Select
              captureMenuScroll={false}
              isOptionDisabled={handleOptionDisabled}
              isSearchable
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              name={`signal-${index}`}
              options={row?.datapoint?.values || []}
              onChange={(e) => handleSignalSelect(e, index)}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              value={row?.signal}
            />
          )}
        </div>
      </div>
      <div className="col-1 pl-1 pl-md-4">
        <button
          className="btn btn-link p-0"
          onClick={() => handleRemoveWidgetSignal(index)}
        >
          <FontAwesomeIcon icon={faTimes} className="text-text-dark" />
        </button>
      </div>
    </div>
  );
});

SignalsCard.propTypes = {
  datapointsMap: PropTypes.arrayOf(PropTypes.object),
  gtemplateID: PropTypes.string,
  handleDatapointSelect: PropTypes.func,
  handleOptionDisabled: PropTypes.func,
  handleRemoveWidgetSignal: PropTypes.func,
  handleSignalSelect: PropTypes.func,
  index: PropTypes.number,
  orgID: PropTypes.string,
  row: PropTypes.shape({
    datapoint: PropTypes.shape({
      value: PropTypes.string,
      values: PropTypes.arrayOf(PropTypes.object),
    }),
    signal: PropTypes.object,
  }),
  updateState: PropTypes.func,
};

SignalsCard.defaultProps = { row: { datapoint: {}, signal: {} } };

export const TagsCard = memo(function TagsCard({
  handleRemoveTag,
  handleTagSelect,
  index,
  row,
  selectedTags,
  tagOptions,
  tagsLoaded,
  tagsLoading,
}) {
  const selectedTagsSet = new Set(selectedTags.map(({ value } = {}) => value));
  return (
    <div
      className="row no-gutters align-items-center bg-background-gray rounded my-3 py-2 py-md-0"
      style={{ minHeight: '5rem' }}
    >
      <div className="col-1 pl-1 px-md-3">
        <span className="fa-layers fa-fw">
          <FontAwesomeIcon icon={faCircle} className="text-white" size="lg" />
          <span className="fa-layers-text signal-number-text font-weight-bold text-muted">
            {index + 1}
          </span>
        </span>
      </div>
      <div className="px-md-0 col-10 col-md-6 form-group mb-0">
        <label htmlFor={`tag-${index}`}>TagName</label>
        <Select
          captureMenuScroll={false}
          isDisabled={!tagsLoaded}
          isLoading={tagsLoading}
          isOptionDisabled={({ value }) => selectedTagsSet.has(value)}
          isSearchable
          maxMenuHeight={200}
          menuPortalTarget={document.body}
          name={`tag-${index}`}
          options={tagOptions}
          onChange={(e) => handleTagSelect(e, index)}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          value={row}
        />
      </div>
      <div className="col-1 pl-1 pl-md-4">
        <button
          className="btn btn-link p-0"
          onClick={() => handleRemoveTag(index)}
        >
          <FontAwesomeIcon icon={faTimes} className="text-text-dark" />
        </button>
      </div>
    </div>
  );
});

TagsCard.propTypes = {
  handleRemoveTag: PropTypes.func,
  handleTagSelect: PropTypes.func,
  index: PropTypes.number,
  row: PropTypes.object,
  selectedTags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  tagOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  tagsLoaded: PropTypes.bool,
  tagsLoading: PropTypes.bool,
};
TagsCard.defaultProps = { row: {}, selectedTags: [], tagOptions: [] };

export const TextStaticConfig = memo(function TextStaticConfig({
  handleAddStaticTextLink,
  handleRemoveStaticTextLink,
  handleStaticTextInput,
  handleStaticTextLinkInput,
  widgetSignals: [{ links, value }],
}) {
  return (
    <div className="row no-gutters align-items-center my-3 rounded">
      <div className="col-12">
        <textarea
          autoComplete="off"
          autoCorrect="off"
          className="form-control nine-tenth-text"
          cols={50}
          minLength={1}
          maxLength={512}
          onChange={handleStaticTextInput}
          placeholder="Enter text here..."
          rows={10}
          spellCheck="false"
          value={value}
        />
      </div>
      <div className="col-12 d-flex justify-content-end my-2">
        {links?.length ? (
          <p className="mr-auto mb-0 nine-tenth-text text-dark">Links</p>
        ) : null}
        <button
          className="btn btn-sm modal-button-primary py-0 three-quarter-text"
          onClick={handleAddStaticTextLink}
        >
          <FontAwesomeIcon className="mr-2" icon={faPlus} />
          Link
        </button>
      </div>
      {links?.map?.(({ display, url }, i) => (
        <div className="col-12 d-flex mb-2" key={i}>
          <input
            className="form-control form-control-sm flex-fill mr-1"
            name="url"
            onChange={(e) => handleStaticTextLinkInput(e, i)}
            placeholder="URL"
            type="text"
            value={url}
          />
          <input
            className="form-control form-control-sm flex-fill ml-1"
            name="display"
            onChange={(e) => handleStaticTextLinkInput(e, i)}
            placeholder="Display Text"
            type="text"
            value={display}
          />
          <button
            className="flex-auto btn btn-link text-danger py-0 d-flex align-items-center justify-content-center"
            onClick={() => handleRemoveStaticTextLink(i)}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        </div>
      ))}
    </div>
  );
});

TextStaticConfig.propTypes = {
  handleAddStaticTextLink: PropTypes.func,
  handleRemoveStaticTextLink: PropTypes.func,
  handleStaticTextInput: PropTypes.func,
  handleStaticTextLinkInput: PropTypes.func,
  widgetSignals: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string })
  ),
};
TextStaticConfig.defaultProps = [{ value: '' }];

export const TimeSinceLastUpdatedToggle = memo(
  function TimeSinceLastUpdatedToggle(props) {
    return props.widgetType === TEXT ? (
      <div className="form-check form-check-inline">
        <input
          checked={props.showTimeSinceLastUpdated}
          className="form-check-input"
          id="lastUpdatedTime"
          onChange={props.handleTimeSinceLastUpdatedClick}
          type="checkbox"
        />
        <label className="form-check-label" htmlFor="lastUpdatedTime">
          Calculate Time Since Last Updated for Timestamp Column
        </label>
      </div>
    ) : null;
  }
);

TimeSinceLastUpdatedToggle.propTypes = {
  handleTimeSinceLastUpdatedClick: PropTypes.func,
  showTimeSinceLastUpdated: PropTypes.bool,
  widgetType: PropTypes.string,
};

export const DisplayTextAsGridToggle = memo(
  function DisplayTextAsGridToggle(props) {
    return props.widgetType === TEXT ? (
      <div className="form-check form-check-inline">
        <input
          checked={props.displayTextAsGrid}
          className="form-check-input"
          id="displayTextAsGrid"
          onChange={props.handleDisplayTextAsGridClick}
          type="checkbox"
        />
        <label className="form-check-label" htmlFor="displayTextAsGrid">
          Display Selected Signal Values on Tile in Large Grid Format
        </label>
      </div>
    ) : null;
  }
);

DisplayTextAsGridToggle.propTypes = {
  displayTextAsGrid: PropTypes.bool,
  handleDisplayTextAsGridClick: PropTypes.func,
  widgetType: PropTypes.string,
};
